/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors } from '@blend/consumer-components';
import { ProvideTimers, TimerProps } from '@blend/shared-react';

export namespace KBAInterstitial {
  export interface Props {
    setContinueButtonDisabled: (disabled: boolean) => void;
  }
}

interface InstructionItemProps {
  number: number;
  title: string;
  text: string;
}

const styles = StyleSheet.create({
  content: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    paddingTop: 24,
    margin: '0 auto',
  },
  instructionsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    width: '100%',
  },
  instructionItem: {
    display: 'flex',
    marginBottom: 24,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  instructionNumber: {
    flex: '0 0 24px',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: colors.catalyst,
    color: colors.white,
    fontSize: 14,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  instructionContent: {
    flex: 1,
  },
  instructionTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: colors.catalyst,
    marginBottom: 4,
  },
  instructionText: {
    fontSize: 14,
    color: colors.steel,
    lineHeight: 1.5,
  },
});

const DEFAULT_DURATION = 10; // 10 seconds

const instructions = [
  {
    title: 'Not all questions will apply to you',
    text: 'You may receive questions that are unrelated to your life or credit history. If so, selecting "None of the Above" may be the correct response. KBA questions often include decoys.',
  },
  {
    title: 'You may receive questions from years ago',
    text: 'Questions may relate to past addresses, loans, or accounts you have not thought about in a while. They may also apply to you if you were, or currently are, a cosigner on a loan. Please take these factors into consideration when selecting your response.',
  },
];

const InstructionItem: React.FC<InstructionItemProps> = ({ number, title, text }) => (
  <li className={css(styles.instructionItem)}>
    <div className={css(styles.instructionNumber)}>{number}</div>
    <div className={css(styles.instructionContent)}>
      <h3 className={css(styles.instructionTitle)}>{title}</h3>
      <p className={css(styles.instructionText)}>{text}</p>
    </div>
  </li>
);

const _KBAInterstitial: React.FC<KBAInterstitial.Props & TimerProps> = ({
  reactSafeSetInterval,
  setContinueButtonDisabled,
}) => {
  const [timeLeft, setTimeLeft] = useState(DEFAULT_DURATION);
  const [shouldEnableContinue, setShouldEnableContinue] = useState(false);

  useEffect(() => {
    if (timeLeft <= 0) {
      // since we need to update the state of the parent component,
      // we need to use a different state variable to trigger the update
      setShouldEnableContinue(true);
      return;
    }

    const timer = reactSafeSetInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (shouldEnableContinue) {
      setContinueButtonDisabled(false);
    }
  }, [shouldEnableContinue, setContinueButtonDisabled]);

  useEffect(() => {
    setContinueButtonDisabled(true);
  }, [setContinueButtonDisabled]);

  return (
    <div className={css(styles.content)}>
      <ul className={css(styles.instructionsList)}>
        {instructions.map((instruction, index) => (
          <InstructionItem key={index} number={index + 1} title={instruction.title} text={instruction.text} />
        ))}
      </ul>
    </div>
  );
};

export const KBAInterstitial = (props: KBAInterstitial.Props) => (
  <ProvideTimers render={timerProps => <_KBAInterstitial {...props} {...timerProps} />} />
);
